import { useEffect, useRef, useState } from "react";

/**
 * TypewriterText - Texte qui s'écrit mot par mot lorsqu'il entre dans le viewport.
 *
 * Modes :
 * - "word"   : chaque mot apparaît progressivement (fondu + léger slide-up)
 * - "letter" : chaque lettre apparaît une à une (effet machine à écrire)
 * - "line"   : chaque ligne apparaît en bloc avec clip-path reveal
 *
 * Usage :
 *   <TypewriterText text="Créativité. Innovation. Excellence." mode="word" />
 *   <TypewriterText text="Valentine Baumberger" mode="letter" speed={60} />
 */

interface TypewriterTextProps {
  text: string;
  mode?: "word" | "letter" | "line";
  speed?: number;       // ms entre chaque unité
  delay?: number;       // ms avant de démarrer
  className?: string;
  style?: React.CSSProperties;
  wordStyle?: React.CSSProperties;  // style appliqué à chaque mot/lettre
  highlightWords?: string[];        // mots à colorer en or
  onComplete?: () => void;
}

export default function TypewriterText({
  text,
  mode = "word",
  speed = 80,
  delay = 0,
  className = "",
  style = {},
  wordStyle = {},
  highlightWords = [],
  onComplete,
}: TypewriterTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  // Découper le texte selon le mode
  const units = mode === "letter"
    ? text.split("")
    : mode === "line"
    ? text.split("\n")
    : text.split(" ");

  // Observer l'entrée dans le viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setTimeout(() => setStarted(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, started]);

  // Animer les unités une à une
  useEffect(() => {
    if (!started) return;
    if (visibleCount >= units.length) {
      onComplete?.();
      return;
    }

    const timer = setTimeout(() => {
      setVisibleCount((c) => c + 1);
    }, speed);

    return () => clearTimeout(timer);
  }, [started, visibleCount, units.length, speed, onComplete]);

  const isHighlighted = (word: string) =>
    highlightWords.some((hw) => word.toLowerCase().includes(hw.toLowerCase()));

  return (
    <div ref={containerRef} className={className} style={{ ...style }}>
      {mode === "letter" ? (
        // Mode lettre : inline, chaque lettre apparaît
        <span aria-label={text}>
          {units.map((char, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                display: "inline-block",
                opacity: i < visibleCount ? 1 : 0,
                transform: i < visibleCount ? "translateY(0)" : "translateY(8px)",
                transition: `opacity 0.2s ease, transform 0.2s ease`,
                whiteSpace: char === " " ? "pre" : "normal",
                color: isHighlighted(char) ? "var(--vea-or, #C9A84C)" : "inherit",
                ...wordStyle,
              }}
            >
              {char === " " ? "\u00A0" : char}
            </span>
          ))}
        </span>
      ) : mode === "line" ? (
        // Mode ligne : chaque ligne avec clip-path reveal
        <span>
          {units.map((line, i) => (
            <span
              key={i}
              style={{
                display: "block",
                clipPath: i < visibleCount ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 100% 0%)",
                transform: i < visibleCount ? "translateY(0)" : "translateY(12px)",
                transition: `clip-path 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)`,
                ...wordStyle,
              }}
            >
              {line}
            </span>
          ))}
        </span>
      ) : (
        // Mode mot : chaque mot apparaît avec fondu + slide-up
        <span>
          {units.map((word, i) => (
            <span
              key={i}
              style={{
                display: "inline-block",
                opacity: i < visibleCount ? 1 : 0,
                transform: i < visibleCount ? "translateY(0)" : "translateY(14px)",
                transition: `opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
                marginRight: "0.28em",
                color: isHighlighted(word) ? "var(--vea-or, #C9A84C)" : "inherit",
                ...wordStyle,
              }}
            >
              {word}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
